import React, { useState, useEffect, useMemo } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import { useAuth } from '../../../../context/AuthContext';
import { StickyNote, Calendar, User, Lock } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { toast } from 'sonner';

/**
 * NotesTab - Internal Team Notes / Customer Interaction Log.
 *
 * This is intentionally separate from the existing single `lead.notes` text
 * field on the lead document. That field is left untouched for backwards
 * compatibility with the lead form. This tab is an append-only, reverse-
 * chronological log of internal team interactions, e.g.:
 *   - "Customer called and was happy with service."
 *   - "Review link sent."
 *   - "Requested afternoon visits."
 *   - "Technician reported gate code issue."
 *
 * Constraints (per v1 product decision):
 *   - Notes are IMMUTABLE — no edit or delete in the UI.
 *   - All roles (incl. technicians) can read and create.
 *   - Storage: companies/{companyId}/leads/{leadId}/notes/{noteId}
 */
const MAX_NOTE_LENGTH = 2000;

const NotesTab = ({ client }) => {
  const { companyId } = useCompany();
  const { user, profile } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const leadId = client?.id;

  useEffect(() => {
    if (!leadId || !db || !companyId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const notesRef = collection(
      db,
      'companies',
      companyId,
      'leads',
      leadId,
      'notes'
    );
    const notesQuery = query(notesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      notesQuery,
      (snapshot) => {
        const notesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotes(notesData);
        setLoading(false);
      },
      (err) => {
        console.error('[NotesTab] Error fetching team notes:', err);
        setError(err.message || 'Failed to load team notes');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, leadId]);

  const createdByName = useMemo(() => {
    return (
      profile?.displayName ||
      profile?.name ||
      [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim() ||
      user?.displayName ||
      (user?.email ? user.email.split('@')[0] : null) ||
      'Unknown User'
    );
  }, [profile, user]);

  const handleAddNote = async (e) => {
    e?.preventDefault?.();
    const content = draft.trim();
    if (!content) return;
    if (!leadId || !companyId) {
      toast.error('Cannot save note — missing lead context');
      return;
    }
    if (!user?.uid) {
      toast.error('You must be signed in to add a note');
      return;
    }
    if (content.length > MAX_NOTE_LENGTH) {
      toast.error(`Notes must be ${MAX_NOTE_LENGTH} characters or fewer`);
      return;
    }

    setSubmitting(true);
    try {
      const notesRef = collection(
        db,
        'companies',
        companyId,
        'leads',
        leadId,
        'notes'
      );
      await addDoc(notesRef, {
        content,
        createdBy: user.uid,
        createdByName,
        createdByEmail: user.email || null,
        createdAt: serverTimestamp(),
      });
      setDraft('');
      toast.success('Note added');
    } catch (err) {
      console.error('[NotesTab] Error saving note:', err);
      toast.error(err?.message || 'Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return 'Just now';
    }
  };

  const remaining = MAX_NOTE_LENGTH - draft.length;
  const canSubmit = draft.trim().length > 0 && !submitting;

  return (
    <div className="space-y-6" data-testid="notes-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Internal Team Notes
          </h3>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Append-only log of internal team interactions for this account.
            Use it for customer calls, technician reports, and follow-ups.
            Notes are timestamped and cannot be edited or deleted.
          </p>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
          <Lock className="h-3.5 w-3.5" />
          <span>Immutable history</span>
        </div>
      </div>

      {/* Composer */}
      <form
        onSubmit={handleAddNote}
        className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
        data-testid="notes-tab-composer"
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Log an interaction… e.g. Customer called and was happy with service."
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          disabled={submitting}
          className="resize-y"
          data-testid="notes-tab-textarea"
        />
        <div className="flex items-center justify-between">
          <span
            className={`text-xs ${
              remaining < 100 ? 'text-amber-600' : 'text-gray-400'
            }`}
            data-testid="notes-tab-char-count"
          >
            {remaining} characters remaining
          </span>
          <Button
            type="submit"
            disabled={!canSubmit}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="notes-tab-add-btn"
          >
            {submitting ? 'Adding…' : 'Add Note'}
          </Button>
        </div>
      </form>

      {/* Feed */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">History</h4>
          <span className="text-xs text-gray-500" data-testid="notes-tab-count">
            {notes.length} {notes.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        {loading ? (
          <div
            className="flex items-center justify-center py-8"
            data-testid="notes-tab-loading"
          >
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--cb-orange)]" />
            <span className="ml-3 text-sm text-gray-600">Loading notes…</span>
          </div>
        ) : error ? (
          <div
            className="text-center py-6 bg-red-50 border border-red-200 rounded-md"
            data-testid="notes-tab-error"
          >
            <p className="text-sm font-medium text-red-700">
              Error loading notes
            </p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        ) : notes.length === 0 ? (
          <div
            className="text-center py-10 border border-dashed border-gray-300 rounded-lg"
            data-testid="notes-tab-empty"
          >
            <StickyNote className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-600">No notes yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Add the first internal note above.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="notes-tab-list">
            {notes.map((note) => (
              <li
                key={note.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                data-testid={`notes-tab-item-${note.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                    <User className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="font-medium truncate">
                      {note.createdByName || 'Unknown User'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(note.createdAt)}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {note.content}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NotesTab;
